import React, { Fragment, PureComponent } from 'react';

import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import Button from '@atlaskit/button/standard-button';
import { uncurlyUuid } from '@atlassian/bitkit-analytics';
import { RelativeDate } from '@atlassian/bitkit-date';

import ApdexMeasuredLink from 'src/components/apdex-measured-link';
import DetailsCard, { Row, Field, Value } from 'src/components/details-card';
import GenericMessage from 'src/components/generic-message';
import RelativeLink from 'src/components/relative-link';
import { Repository, User } from 'src/components/types';
import { SourceRepoDetailsActionClicked } from 'src/sections/repository/facts';
import { ApdexTask } from 'src/types/apdex';
import { publishFact, publishUiEvent } from 'src/utils/analytics/publish';

import { RepositorySizeLimits } from '../reducers/section';
import { RepositoryPrivilege } from '../types';
import urls from '../urls';

import DetailsList from './details-list';
import { DetailsList as DetailsListStyle } from './details-list.style';
import ForkListItem from './fork-list-item';
import LargeRepositoryWarning from './large-repository-warning';
import messages from './repository-details-card.i18n';
import * as styles from './repository-details-card.style';
import RepositoryDetailsModal from './repository-details-modal';
import WatcherListItem from './watcher-list-item';

type RepositoryDetailsCardProps = {
  accessLevel?: RepositoryPrivilege;
  branchCount?: number;
  commitsBehindParent?: number;
  forkCount?: number;
  intl: InjectedIntl;
  hasError: boolean;
  isCollapsed: boolean;
  isLoading: boolean;
  isReadOnly?: boolean;
  language?: string;
  lastUpdated?: Date;
  onErrorClick: () => void;
  onOpenSyncDialog?: () => void;
  openPullRequestCount?: number;
  parent?: Repository;
  repository?: Repository;
  size?: number;
  watcherCount?: number;
  sizeLimits: RepositorySizeLimits;
};

const renderForks = (items: Repository[]) => (
  <DetailsListStyle>
    {items.map(item => (
      <ForkListItem item={item} key={item.uuid} />
    ))}
  </DetailsListStyle>
);

export const renderDetailsList = (owner: string, slug: string) => (
  <DetailsList
    requestUrl={urls.api.v20.forks(owner, slug)}
    render={renderForks}
  />
);

class RepositoryDetailsCard extends PureComponent<RepositoryDetailsCardProps> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    isCollapsed: false,
    onOpenSyncDialog: () => {},
  };

  privilegeLabels = {
    admin: <FormattedMessage {...messages.privilegeAdmin} />,
    read: <FormattedMessage {...messages.privilegeRead} />,
    write: <FormattedMessage {...messages.privilegeWrite} />,
    none: <FormattedMessage {...messages.privilegeRead} />,
  };

  publishDetailsAnalyticEvent = (label: string, repoUUID: string) => {
    publishFact(
      new SourceRepoDetailsActionClicked({
        label,
        repository_uuid: repoUUID,
        frontbucket_view: true,
      })
    );
  };

  renderWatchers = (items: User[]) => (
    <DetailsListStyle>
      {items.map(item => (
        <WatcherListItem item={item} key={item.uuid} />
      ))}
    </DetailsListStyle>
  );

  renderContent() {
    const {
      accessLevel,
      branchCount,
      commitsBehindParent,
      forkCount,
      hasError,
      intl,
      isLoading,
      isReadOnly,
      language,
      lastUpdated,
      onErrorClick,
      openPullRequestCount,
      parent,
      repository,
      size,
      watcherCount,
      sizeLimits,
    } = this.props;

    if (!repository || isLoading) {
      return null;
    }

    if (hasError) {
      return (
        <GenericMessage
          iconType="warning"
          title={<FormattedMessage {...messages.errorHeading} />}
        >
          <CustomThemeButton appearance="link" onClick={onErrorClick}>
            <FormattedMessage {...messages.errorAction} />
          </CustomThemeButton>
        </GenericMessage>
      );
    }

    const [owner, slug] = repository.full_name.split('/');

    const syncButtonContent = intl.formatMessage(messages.syncButton, {
      commitsBehind: commitsBehindParent,
    });

    const publishAnalyticsEvent = (label: string, actionSubjectId: string) => {
      this.publishDetailsAnalyticEvent(label, uncurlyUuid(repository.uuid));
      publishUiEvent({
        source: 'repositoryDetailsCard',
        action: 'clicked',
        actionSubject: 'link',
        actionSubjectId,
        objectType: 'repository',
        objectId: uncurlyUuid(repository.uuid),
        containerType: 'workspace',
        containerId: repository.workspace
          ? uncurlyUuid(repository.workspace.uuid)
          : '',
        attributes: {
          frontbucketView: true,
        },
      });
    };

    return (
      <Fragment>
        {(lastUpdated || language) && (
          <Row>
            {lastUpdated && (
              <Field
                label={<FormattedMessage {...messages.lastUpdatedLabel} />}
                value={<RelativeDate date={lastUpdated} />}
              />
            )}

            {language && (
              <Field
                label={<FormattedMessage {...messages.languageLabel} />}
                value={language}
              />
            )}
          </Row>
        )}

        <Row>
          <Field
            isFocusable
            label={
              <FormattedMessage {...messages.openPullRequestsCountLabel}>
                {message => <span aria-hidden="true">{message}</span>}
              </FormattedMessage>
            }
            value={
              <ApdexMeasuredLink
                to={urls.ui.pullRequests(owner, slug)}
                onClick={() =>
                  publishAnalyticsEvent('pull_requests', 'openPullRequestsLink')
                }
                task={ApdexTask.PullRequests}
                aria-label={intl.formatMessage(
                  messages.openPullRequestsAriaLabel,
                  {
                    count: openPullRequestCount,
                  }
                )}
              >
                {openPullRequestCount}
              </ApdexMeasuredLink>
            }
          />
          <Field
            isFocusable
            label={
              <FormattedMessage {...messages.branchesCountLabel}>
                {message => <span aria-hidden="true">{message}</span>}
              </FormattedMessage>
            }
            value={
              <ApdexMeasuredLink
                to={urls.ui.branches(owner, slug)}
                onClick={() =>
                  publishAnalyticsEvent('branches', 'openBranchesLink')
                }
                task={ApdexTask.Branches}
                aria-label={intl.formatMessage(
                  messages.branchesCountAriaLabel,
                  {
                    count: branchCount,
                  }
                )}
              >
                {branchCount}
              </ApdexMeasuredLink>
            }
          />
        </Row>

        <Row>
          <Field
            isFocusable
            label={
              <FormattedMessage {...messages.watchersCountLabel}>
                {message => <span aria-hidden={!!watcherCount}>{message}</span>}
              </FormattedMessage>
            }
            value={
              watcherCount ? (
                <RepositoryDetailsModal
                  heading={intl.formatMessage(
                    messages.watchersCountLabelWithValue,
                    {
                      count: watcherCount,
                    }
                  )}
                  modalTrigger={onClick => (
                    <Button
                      onClick={onClick}
                      appearance="link"
                      spacing="none"
                      aria-label={intl.formatMessage(
                        messages.watchersCountAriaLabel,
                        {
                          count: watcherCount,
                        }
                      )}
                    >
                      <Value>{watcherCount}</Value>
                    </Button>
                  )}
                  modalTriggerCallback={() =>
                    publishAnalyticsEvent('watchers', 'openWatchersModalLink')
                  }
                >
                  <DetailsList
                    requestUrl={urls.api.v20.watchers(owner, slug)}
                    render={this.renderWatchers}
                  />
                </RepositoryDetailsModal>
              ) : (
                watcherCount
              )
            }
          />
          <Field
            isFocusable
            label={
              <FormattedMessage {...messages.forksCountLabel}>
                {message => <span aria-hidden={!!forkCount}>{message}</span>}
              </FormattedMessage>
            }
            value={
              forkCount ? (
                <RepositoryDetailsModal
                  heading={intl.formatMessage(
                    messages.forksCountLabelWithValue,
                    {
                      count: forkCount,
                    }
                  )}
                  modalTrigger={onClick => (
                    <Button
                      onClick={onClick}
                      appearance="link"
                      spacing="none"
                      aria-label={intl.formatMessage(
                        messages.forksCountAriaLabel,
                        {
                          count: forkCount,
                        }
                      )}
                    >
                      <Value>{forkCount}</Value>
                    </Button>
                  )}
                  modalTriggerCallback={() =>
                    publishAnalyticsEvent('forks', 'openForksModalLink')
                  }
                >
                  {renderDetailsList(owner, slug)}
                </RepositoryDetailsModal>
              ) : (
                forkCount
              )
            }
          />
        </Row>

        {repository.website && (
          <Row>
            <Field
              label={<FormattedMessage {...messages.websiteLabel} />}
              value={
                <a
                  href={repository.website}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                >
                  {repository.website}
                </a>
              }
            />
          </Row>
        )}

        {accessLevel && (
          <Row>
            <Field
              label={<FormattedMessage {...messages.accessLevelLabel} />}
              value={this.privilegeLabels[accessLevel]}
            />
          </Row>
        )}

        {size && size >= sizeLimits.soft && (
          <Row>
            <Field
              label={<FormattedMessage {...messages.sizeLabel} />}
              value={
                <LargeRepositoryWarning
                  size={size}
                  isReadOnly={isReadOnly}
                  accessLevel={accessLevel}
                />
              }
            />
          </Row>
        )}

        {parent && (
          <Fragment>
            <Row>
              <Field
                label={<FormattedMessage {...messages.forkOfLabel} />}
                value={
                  <RelativeLink to={`/${parent.full_name}/`}>
                    {parent.full_name}
                  </RelativeLink>
                }
              />
            </Row>
            {!!commitsBehindParent && (
              <Row>
                <styles.SyncButtonTooltip content={syncButtonContent}>
                  <styles.SyncButton onClick={this.props.onOpenSyncDialog}>
                    {syncButtonContent}
                  </styles.SyncButton>
                </styles.SyncButtonTooltip>
              </Row>
            )}
          </Fragment>
        )}
      </Fragment>
    );
  }

  render() {
    const { intl, isCollapsed, isLoading, repository } = this.props;
    const label = intl.formatMessage(messages.cardLabel);

    return (
      <DetailsCard
        isLoading={!repository || isLoading}
        label={label}
        isCollapsed={isCollapsed}
      >
        {this.renderContent()}
      </DetailsCard>
    );
  }
}

export default injectIntl(RepositoryDetailsCard);
